// Connectivity state for the offline-first data layer.
//
// Single source of truth for:
//   - whether the device thinks it has network (online/offline events)
//   - whether the outbox is currently draining (syncing)
//   - how many mutations are queued waiting to sync (pendingCount)
//   - when the last successful drain finished (lastSyncAt)
//
// The provider owns the online/offline event listeners and triggers an
// outbox drain on reconnect. Domain hooks (useHabits, useCompletions,
// useSickWeeks) register id-resolved / rejected callbacks via
// registerIdResolver / registerRejectionHandler so they can keep their
// local stores and React state in sync with what the outbox does.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { drain as drainOutbox, pendingCount as outboxPendingCount } from '../offline/outbox';
import { getApiUrl } from '../utils/apiHelpers';
import { logger } from '../utils/logger';

const log = logger.child('Connectivity');

const ConnectivityContext = createContext(null);

const initialOnline = typeof navigator === 'undefined' ? true : navigator.onLine !== false;

export const ConnectivityProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(initialOnline);
  const [syncState, setSyncState] = useState('idle'); // 'idle' | 'syncing' | 'failed'
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncAt, setLastSyncAt] = useState(null);

  const idResolvers = useRef(new Set());
  const rejectionHandlers = useRef(new Set());
  const drainListeners = useRef(new Set());
  const drainInFlight = useRef(false);

  const refreshPending = useCallback(async () => {
    try {
      const n = await outboxPendingCount();
      setPendingCount(n);
    } catch (e) {
      log.warn('outbox pending count failed', e);
    }
  }, []);

  const runDrain = useCallback(async () => {
    if (drainInFlight.current) return null;
    const token = (() => {
      try { return localStorage.getItem('token'); } catch { return null; }
    })();
    if (!token) {
      await refreshPending();
      return null;
    }
    drainInFlight.current = true;
    setSyncState('syncing');
    try {
      const result = await drainOutbox({
        apiBase: getApiUrl(),
        token,
        onIdResolved: async (tempId, realId, serverHabit) => {
          for (const fn of idResolvers.current) {
            try { await fn(tempId, realId, serverHabit); }
            catch (e) { log.warn('idResolver threw', e); }
          }
        },
        onRejected: async (row, status) => {
          for (const fn of rejectionHandlers.current) {
            try { await fn(row, status); }
            catch (e) { log.warn('rejection handler threw', e); }
          }
        },
      });
      await refreshPending();
      if (result.deferred > 0) {
        setSyncState('failed');
      } else {
        setSyncState('idle');
        setLastSyncAt(new Date().toISOString());
      }
      for (const fn of drainListeners.current) {
        try { fn(result); } catch (e) { log.warn('drain listener threw', e); }
      }
      return result;
    } catch (e) {
      log.warn('drain threw', e);
      setSyncState('failed');
      await refreshPending();
      return null;
    } finally {
      drainInFlight.current = false;
    }
  }, [refreshPending]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      runDrain();
    };
    const handleOffline = () => {
      setIsOnline(false);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    // Initial pending count on mount.
    refreshPending();
    // If we boot already online with a non-empty queue (e.g. browser was killed
    // before a previous drain finished), kick one off.
    if (initialOnline) runDrain();
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [refreshPending, runDrain]);

  const registerIdResolver = useCallback((fn) => {
    idResolvers.current.add(fn);
    return () => idResolvers.current.delete(fn);
  }, []);

  const registerRejectionHandler = useCallback((fn) => {
    rejectionHandlers.current.add(fn);
    return () => rejectionHandlers.current.delete(fn);
  }, []);

  const registerDrainListener = useCallback((fn) => {
    drainListeners.current.add(fn);
    return () => drainListeners.current.delete(fn);
  }, []);

  // Called by hooks immediately after enqueueing a mutation, so the badge
  // updates without waiting for a drain cycle to refresh the count.
  const notifyEnqueued = useCallback(() => {
    refreshPending();
    if (isOnline) runDrain();
  }, [isOnline, refreshPending, runDrain]);

  const value = useMemo(() => ({
    isOnline,
    syncState,
    pendingCount,
    lastSyncAt,
    retryNow: runDrain,
    notifyEnqueued,
    registerIdResolver,
    registerRejectionHandler,
    registerDrainListener,
  }), [isOnline, syncState, pendingCount, lastSyncAt, runDrain, notifyEnqueued, registerIdResolver, registerRejectionHandler, registerDrainListener]);

  return (
    <ConnectivityContext.Provider value={value}>
      {children}
    </ConnectivityContext.Provider>
  );
};

export const useConnectivity = () => {
  const ctx = useContext(ConnectivityContext);
  if (!ctx) throw new Error('useConnectivity must be used within a ConnectivityProvider');
  return ctx;
};

export default ConnectivityContext;
